import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useLibrary } from '../lib/Library';
import { AUDIO_LANGUAGES } from '../lib/playback';
import LiveRelayPanel from './LiveRelayPanel';

const GB = 1024 * 1024 * 1024;

const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exp = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** exp;
  return `${value >= 10 || exp === 0 ? Math.round(value) : value.toFixed(1)} ${units[exp]}`;
};

// Each toggle explains what it costs in disk, since that is the only reason
// any of them exist
const TOGGLES = [
  {
    key: 'selectOnlyPlayedFile',
    label: 'Download only what you play',
    note: 'Skips the rest of a season pack. Without this a 12-episode torrent downloads in full to watch one episode.'
  },
  {
    key: 'purgeOnNewStream',
    label: 'Clear cache when you start something new',
    note: 'Deletes the previous title as soon as you pick another. Keeps the cache flat instead of growing all evening.'
  },
  {
    key: 'purgeOnStartup',
    label: 'Clear cache on server start',
    note: 'Anything left from a previous run is a partial download nothing is watching.'
  }
];

const NUMBERS = [
  { key: 'cacheCapGB', label: 'CACHE LIMIT', unit: 'GB', min: 1, max: 500 },
  { key: 'minFreeDiskGB', label: 'KEEP FREE', unit: 'GB', min: 1, max: 500 },
  { key: 'idleEvictionMinutes', label: 'DROP IDLE AFTER', unit: 'min', min: 1, max: 1440 },
  { key: 'maxConcurrent', label: 'MAX TORRENTS', unit: '', min: 1, max: 10 }
];

// What pressing Play may pick on its own. Every tier the indexers return stays
// in the player's quality menu regardless — this only governs the unasked pick.
const QUALITY_CHOICES = [
  {
    value: '1080p',
    label: 'Up to 1080p',
    note: 'Fastest to start. Plenty for most screens.'
  },
  {
    value: '1440p',
    label: 'Up to 1440p',
    note: 'The default: automatic plays pick the best 1080p or 1440p release.'
  },
  {
    value: '2160p',
    label: 'Allow 4K',
    note: '4K files are the biggest and their swarms the thinnest — starts are measurably slower unless the release is cached on debrid.'
  }
];

const Data = () => {
  const { maxAutoResolution, setMaxAutoResolution, audioLanguage, setAudioLanguage } = useLibrary();
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [purging, setPurging] = useState(false);
  const [error, setError] = useState('');
  const [flash, setFlash] = useState('');

  const load = useCallback(
    () =>
      axios
        .get('/api/storage')
        .then(({ data }) => {
          setSettings(data.data.settings);
          setStats(data.data.stats);
          setError('');
        })
        .catch(() => setError('Failed to load storage status')),
    []
  );

  useEffect(() => {
    load().finally(() => setLoading(false));
    // Usage moves while something is streaming, so keep the number honest
    const tick = setInterval(load, 5000);
    return () => clearInterval(tick);
  }, [load]);

  const save = (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    setSaving(true);
    axios
      .put('/api/storage/settings', patch)
      .then(({ data }) => {
        setSettings(data.data);
        setFlash('Saved');
        setTimeout(() => setFlash(''), 1600);
        return load();
      })
      .catch(() => setError('Failed to save settings'))
      .finally(() => setSaving(false));
  };

  const purge = () => {
    setPurging(true);
    axios
      .post('/api/storage/purge')
      .then(({ data }) => {
        setFlash(`Freed ${formatBytes(data.data.freedBytes)}`);
        setTimeout(() => setFlash(''), 2600);
        return load();
      })
      .catch(() => setError('Failed to purge cache'))
      .finally(() => setPurging(false));
  };

  if (loading) return <div className="page loading">LOADING STORAGE</div>;
  if (error && !settings) return <div className="page alert">{error}</div>;

  const used = stats?.usedBytes || 0;
  const cap = stats?.capBytes || GB;
  const pct = Math.min(100, Math.round((used / cap) * 100));
  const low = (stats?.freeBytes || 0) < (settings?.minFreeDiskGB || 0) * GB;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Settings</h1>
          <p className="page-sub">
            how playback chooses, and what streaming keeps on disk
          </p>
        </div>
        <button className="btn btn--sm" onClick={purge} disabled={purging}>
          {purging ? 'Clearing…' : 'Clear cache now'}
        </button>
      </div>

      {low && (
        <div className="notice">
          <b>Disk is below your minimum</b>
          Only {formatBytes(stats?.freeBytes)} free, and new streams are blocked below{' '}
          {settings.minFreeDiskGB} GB. Clear the cache or lower the threshold.
        </div>
      )}

      <div className="stats" style={{ marginBottom: 30 }}>
        <div className="stat">
          <div className="stat-k">CACHE</div>
          <div className="stat-v">
            <span className="stat-big">{formatBytes(used)}</span>
            <span className="stat-unit">of {settings.cacheCapGB} GB limit</span>
          </div>
        </div>
        <div className="stat">
          <div className="stat-k">FREE ON DISK</div>
          <div className="stat-v">
            <span className="stat-big">{formatBytes(stats?.freeBytes)}</span>
            <span className="stat-unit">available</span>
          </div>
        </div>
        <div className="stat">
          <div className="stat-k">STREAMING</div>
          <div className="stat-v">
            <span className="stat-big">{stats?.activeTorrents ?? 0}</span>
            <span className="stat-unit">active torrents</span>
          </div>
        </div>
      </div>

      <div className="usage-bar" aria-label={`${pct}% of cache limit used`}>
        <span style={{ width: `${pct}%` }} className={pct >= 90 ? 'is-full' : undefined} />
      </div>

      <section className="section">
        <div className="section-head">
          <h3 className="section-title">Behaviour</h3>
          <span className="section-note">{saving ? 'saving…' : flash || 'saved automatically'}</span>
        </div>

        {TOGGLES.map((toggle) => (
          <label key={toggle.key} className="opt">
            <input
              type="checkbox"
              checked={Boolean(settings[toggle.key])}
              onChange={(event) => save({ [toggle.key]: event.target.checked })}
            />
            <span className="opt-body">
              <span className="opt-label">{toggle.label}</span>
              <span className="opt-note">{toggle.note}</span>
            </span>
          </label>
        ))}
      </section>

      <section className="section">
        <div className="section-head">
          <h3 className="section-title">Playback quality</h3>
          <span className="section-note">
            what pressing Play picks on its own — every quality stays in the player menu
          </span>
        </div>

        {QUALITY_CHOICES.map((choice) => (
          <label key={choice.value} className="opt">
            <input
              type="radio"
              name="maxAutoResolution"
              checked={maxAutoResolution === choice.value}
              onChange={() => setMaxAutoResolution(choice.value)}
            />
            <span className="opt-body">
              <span className="opt-label">{choice.label}</span>
              <span className="opt-note">{choice.note}</span>
            </span>
          </label>
        ))}
      </section>

      <section className="section">
        <div className="section-head">
          <h3 className="section-title">Audio language</h3>
          <span className="section-note">
            for this profile — the player can switch any film to another language mid-stream
          </span>
        </div>

        <label className="field lang-field">
          <span>PLAY IN</span>
          <select value={audioLanguage} onChange={(event) => setAudioLanguage(event.target.value)}>
            {AUDIO_LANGUAGES.map((lang) => (
              <option key={lang.code} value={lang.code}>
                {lang.label}
              </option>
            ))}
          </select>
        </label>
        <p className="opt-note lang-note">
          {audioLanguage === 'original'
            ? 'Everything plays in the language it was made in. Dubbed releases are never picked.'
            : 'Play finds a dubbed release in this language first. A title nobody has dubbed plays in its original language instead.'}
        </p>
      </section>

      <section className="section">
        <div className="section-head">
          <h3 className="section-title">Limits</h3>
          <span className="section-note">applied as soon as you change them</span>
        </div>

        <div className="limits">
          {NUMBERS.map((num) => (
            <label key={num.key} className="field limit">
              <span>
                {num.label}
                {num.unit ? ` (${num.unit})` : ''}
              </span>
              <input
                type="number"
                min={num.min}
                max={num.max}
                value={settings[num.key]}
                onChange={(event) => setSettings({ ...settings, [num.key]: event.target.value })}
                onBlur={(event) => {
                  const value = Number(event.target.value);
                  if (Number.isFinite(value)) save({ [num.key]: value });
                }}
              />
            </label>
          ))}
        </div>
      </section>

      <section className="section">
        <div className="section-head">
          <h3 className="section-title">On disk</h3>
          <span className="section-note">{stats?.cacheDir}</span>
        </div>

        {stats?.entries?.length ? (
          <div className="facts" style={{ marginTop: 0 }}>
            {stats.entries.map((entry) => (
              <div key={entry.name} className="disk-row">
                <div className="fact-v disk-name">{entry.name}</div>
                <div className="fact-k disk-size">{formatBytes(entry.bytes)}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty">Cache is empty</div>
        )}
      </section>

      <LiveRelayPanel />
    </div>
  );
};

export default Data;
